import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AuthForm from './components/AuthForm';
import SecurityBadges from './components/SecurityBadges';
import TrustIndicators from './components/TrustIndicators';
import AnimatedBackground from './components/AnimatedBackground';
import Icon from '../../components/AppIcon';

const Authentication = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already authenticated
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated === 'true') {
      navigate('/dashboard');
    }
  }, [navigate]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <AnimatedBackground />
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => navigate('/home-landing')}
          >
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                <Icon name="Brain" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full animate-pulse-soft"></div>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-foreground leading-none">
                Ipsos Insight
              </span>
              <span className="text-sm text-text-secondary font-medium">
                Intelligence Platform
              </span>
            </div>
          </motion.div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full max-w-6xl mx-auto"
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Branding & Trust */}
              <motion.div variants={itemVariants} className="space-y-8">
                <div className="text-center lg:text-left">
                  <motion.h1
                    variants={itemVariants}
                    className="text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight"
                  >
                    Secure Access to
                    <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                      Market Intelligence
                    </span>
                  </motion.h1>
                  
                  <motion.p
                    variants={itemVariants}
                    className="text-lg text-text-secondary mb-8 leading-relaxed"
                  >
                    Join thousands of research professionals using AI-powered automation 
                    to transform market research workflows. Enterprise-grade security 
                    meets cutting-edge intelligence.
                  </motion.p>

                  {/* Key Features */}
                  <motion.div variants={itemVariants} className="space-y-4 mb-8">
                    {[
                      { icon: "Zap", text: "AI-Powered Research Automation" },
                      { icon: "Shield", text: "Enterprise Security & Compliance" },
                      { icon: "BarChart3", text: "Real-time Data Visualization" },
                      { icon: "Users", text: "Collaborative Team Workflows" }
                    ]?.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name={feature?.icon} size={16} className="text-primary" />
                        </div>
                        <span className="text-foreground font-medium">{feature?.text}</span>
                      </div>
                    ))}
                  </motion.div>
                </div>

                {/* Trust Indicators - Hidden on mobile */}
                <motion.div variants={itemVariants} className="hidden lg:block">
                  <TrustIndicators />
                </motion.div>
              </motion.div>

              {/* Right Column - Authentication Form */}
              <motion.div variants={itemVariants} className="flex justify-center">
                <AuthForm />
              </motion.div>
            </div>

            {/* Security Badges */}
            <motion.div variants={itemVariants} className="mt-16">
              <SecurityBadges />
            </motion.div>

            {/* Mobile Trust Indicators */}
            <motion.div variants={itemVariants} className="lg:hidden mt-12">
              <TrustIndicators />
            </motion.div>
          </motion.div>
        </main>

        {/* Footer */}
        <footer className="p-6 border-t border-border/50">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0"
          >
            <div className="flex items-center space-x-6 text-sm text-text-secondary">
              <button className="hover:text-foreground transition-colors duration-200">
                Privacy Policy
              </button>
              <button className="hover:text-foreground transition-colors duration-200">
                Terms of Service
              </button>
              <button className="hover:text-foreground transition-colors duration-200">
                Security
              </button>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-text-secondary">
                © {new Date()?.getFullYear()} Ipsos Insight Intelligence Platform
              </span>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse-soft"></div>
                <span className="text-xs text-success font-medium">All Systems Operational</span>
              </div>
            </div>
          </motion.div>
        </footer>
      </div>
    </div>
  );
};

export default Authentication;